<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapState, mapWritableState } from 'pinia';
import { usePoolsStore } from '@/stores/pools';
import Decimal from 'decimal.js-light';
import TableFiller from '@/components/table-filler.vue';
import PoolsAdd from '@/components/pools/pools/pools-add.vue';
import { catchError } from '@/helpers/toast';
import { updatePool, deletePool } from '@/api/admin-be';
import PoolsList from '@/components/pools/pools/pools-list.vue';
import PoolsFilters from '@/components/pools/pools/pools-filters.vue';
import type { UpdatePool } from '@/types/pools';
import Pagination from '@/components/pagination.vue';

export default defineComponent({
  name: 'pools',
  components: { Pagination, PoolsList, PoolsAdd, TableFiller, PoolsFilters },
  computed: {
    ...mapState(usePoolsStore, ['pools', 'poolsCount', 'poolTypeIds']),
    ...mapWritableState(usePoolsStore, ['poolsOffset', 'poolsSize', 'currentPoolId', 'filterPoolId']),
    poolsPrecalculated() {
      if (!this.pools) return {};

      const precalculated = {};

      for (const pool of this.pools) {
        const jackpotAmount = this.getPoolJackpotAmount(pool);
        const jackpotWins = this.getPoolJackpotWins(pool);
        precalculated[pool.id] = {
          jackpotAmount: jackpotAmount,
          jackpotCarryover: jackpotAmount.plus(jackpotWins),
          stakesAmount: this.getPoolStakesAmount(pool),
          winsAmount: this.getPoolWinsAmount(pool),
        };
      }

      return precalculated;
    },
  },
  data() {
    return {
      statuses: {
        null: 'Черновик',
        Scheduled: 'Приём ставок',
        InProgress: 'Разыгрывается',
        Completed: 'Завершён',
        Cancelled: 'Отменён',
      },
      statusColors: {
        null: 'text-gray-500',
        Scheduled: 'text-blue-500',
        InProgress: 'text-yellow-500',
        Completed: 'text-green-500',
        Cancelled: 'text-red-500',
      },
      nullStatus: 'Черновик',
    };
  },
  methods: {
    ...mapActions(usePoolsStore, ['fetchPools', 'fetchPoolTypeIds']),
    getPoolJackpotAmount(pool) {
      const jackpotTxs = pool.poolTransactions.filter(({ type }) => type === 'Jackpot');

      return jackpotTxs.reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0));
    },
    getPoolJackpotWins(pool) {
      const jackpotTxs = [];

      for (const bet of pool.poolBets) {
        for (const tx of bet.poolBetTransactions) {
          if (tx.type === 'Jackpot') jackpotTxs.push(tx);
        }
      }

      return jackpotTxs.reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0));
    },
    getPoolStakesAmount(pool) {
      return pool.poolBets.reduce(
        (acc, bet) =>
          acc.plus(
            bet.poolBetTransactions
              .filter((tx) => tx.type === 'Stake' && tx.amount > 0)
              .reduce((acc, tx) => acc.plus(tx.vendorAmount), new Decimal(0))
          ),
        new Decimal(0)
      );
    },
    getPoolWinsAmount(pool) {
      return pool.poolBets.reduce(
        (acc, bet) =>
          acc.plus(
            bet.poolBetTransactions
              .filter((tx) => tx.type === 'Stake' && tx.amount <= 0)
              .reduce((acc, tx) => acc.minus(tx.vendorAmount), new Decimal(0))
          ),
        new Decimal(0)
      );
    },
    updatePool(poolId: number, data: UpdatePool) {
      return updatePool(poolId, data)
        .then(() => this.fetchPools())
        .catch((error) => catchError('Ошибка обновления пула', error));
    },
    startPool(poolId: number) {
      return this.updatePool(poolId, { status: 'Scheduled' });
    },
    stopPool(poolId: number) {
      return this.updatePool(poolId, { status: null });
    },
    deletePool(poolId: number) {
      return deletePool(poolId)
        .then(() => this.fetchPools())
        .catch((error) => catchError('Ошибка удаления пула', error));
    },
    flipPoolsPage(newOffset: number) {
      this.poolsOffset = newOffset;
      this.currentPoolId = null;
      this.fetchPools();
    },
    resizePoolsPage(newSize: number) {
      this.poolsSize = newSize;
      this.fetchPools();
    },
  },
  mounted() {
    if (!this.poolTypeIds) {
      this.fetchPoolTypeIds();
    }
    if (!this.pools) {
      this.fetchPools();
    }
  },
});
</script>

<template>
  <div class="flex">
    <pools-filters v-model="filterPoolId"/>
  </div>
  <pools-list>
    <pools-add />
  </pools-list>
  <div class="text-center">
    <pagination
      :offset="poolsOffset"
      :size="poolsSize"
      :count="poolsCount"
      :resizable="true"
      :customSizes="[5, 10, 15, 20]"
      @flip="flipPoolsPage"
      @resize="resizePoolsPage"
    />
  </div>
</template>

<style scoped lang="scss"></style>